'use client';

import { Gift } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { computeDiscountOff } from '@kit/booking-addons/lib/compute-discount';
import { formatCurrency } from '@kit/shared/currency';
import { CurrencyIcon, useTenantCurrency } from '@kit/ui/currency-icon';
import { Trans } from '@kit/ui/trans';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';

import type { PublicDiscountCode } from '../../book/_lib/public-discount-codes';

export function NativePackageOffers({
  price,
  offers,
}: {
  price: number;
  offers: PublicDiscountCode[];
}) {
  const t = useNativeTokens();
  const currency = useTenantCurrency();
  const { i18n } = useTranslation('addons');

  return offers.map((offer) => {
    const amountOff = computeDiscountOff({
      discountType: offer.discount_type,
      discountValue: offer.discount_value,
      base: price,
      netRemaining: price,
    });
    const finalPrice = Math.round(Math.max(0, price - amountOff) * 100) / 100;

    return (
      <div
        key={offer.id}
        style={{
          display: 'inline-flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '4px 6px',
          maxWidth: '100%',
          padding: '6px 11px',
          background: t.accent,
          borderRadius: t.radius,
          fontFamily: t.fontSans,
          fontSize: 11,
          color: t.background,
        }}
        data-test="native-package-offers"
      >
        <Gift
          aria-hidden="true"
          style={{ width: 12, height: 12, strokeWidth: 1.5, flexShrink: 0 }}
        />
        <Trans
          i18nKey="addons:customerDiscount.offers.priceWithCode"
          components={{
            price: (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 14,
                  fontWeight: 600,
                }}
                data-test="native-package-offer-final-price"
              >
                <CurrencyIcon className="h-3 w-3 shrink-0" />
                {formatCurrency(finalPrice, currency, i18n.language, {
                  style: 'decimal',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })}
              </span>
            ),
            code: (
              <span
                dir="ltr"
                style={{
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                  fontFamily: t.fontMono,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                }}
              >
                {offer.code}
              </span>
            ),
          }}
        />
      </div>
    );
  });
}
